// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { featureIsEnabled } from '@ta/common.features';
import classNames from '@ta/common.classnames';
import type { ViewportCategory } from '@ta/common.state';
import Popover from '@ta/overlays.popover';
import type { ExternalRepostProps } from '@ta/social.types';
import ShareTracker from './ShareTracker';
import ShareViaSms from './ShareViaSms';
import ShareViaEmail from './ShareViaEmail';
import ShareViaClipboard from './ShareViaClipboard';
import ShareViaRepostAction from './ShareViaRepostAction';
import styles from './SharePopover.less';

type Props = {
  /** The URL to share */
  url: string,
  /** For use in tracking: What type of item are we sharing? */
  itemType: string,
  /** For use in tracking: What is the ID of the item? e.g. if it is a POI, what is the locationID */
  itemId: string,
  /** Whether or not to treat the attachment as fixed */
  fixed: boolean,
  /** A pixel shift in the x dimension */
  shiftX: number,
  /** A pixel shift in the y dimension */
  shiftY: number,
  /** A header element to show above the menu items */
  headerComponent?: React.Node,
  /** Which edge to align the popup */
  alignEdge?: 'right' | 'left',
  /** Optional repost props */
  repostProps?: ExternalRepostProps,
  /** Should the popover open above? Defaults to below if set to false or omitted */
  above?: boolean,
  /** The Render prop, passed callbacks to set the target of attachment and open/close, and the state of the popover */
  children: (
    target: (?HTMLElement) => void,
    open: () => void,
    close: () => void,
    isOpen: boolean,
  ) => React.Node,
};

type DropdownExternalProps = {|
  /** The URL to share */
  url: string,
  /** For use in tracking: What type of item are we sharing? */
  itemType: string,
  /** For use in tracking: What is the ID of the item? e.g. if it is a POI, what is the locationID */
  itemId: string,
  /** A header element to show above the menu items */
  headerComponent?: React.Node,
  /** Optional repost props */
  repostProps?: ExternalRepostProps,
|};

type DropdownProps = {|
  ...DropdownExternalProps,
  /** The device type from the redux store. Don't worry about this */
  viewportCategory: ViewportCategory,
|};

const ShareDropdownMenu = ({
  url,
  itemType,
  itemId,
  viewportCategory,
  headerComponent,
  repostProps,
}: DropdownProps) => (
  <div
    className={classNames(
      styles.container,
      featureIsEnabled('hr_dark_green_icons') ? styles.dark_icons : '',
    )}
  >
    {headerComponent}
    {viewportCategory === 'MOBILE' && (
      <ShareTracker action="share_select" itemType={itemType} itemId={itemId} url={url} type="sms">
        {urlWithMcid => <ShareViaSms url={urlWithMcid} itemType={itemType} size="small" />}
      </ShareTracker>
    )}
    <ShareTracker url={url} itemType={itemType} itemId={itemId} action="share_select" type="email">
      {urlWithMcid => (
        <ShareViaEmail url={urlWithMcid} itemType={itemType} itemId={Number(itemId)} size="small" />
      )}
    </ShareTracker>
    <ShareTracker
      action="share_select"
      itemType={itemType}
      itemId={itemId}
      url={url}
      type="clipboard"
    >
      {urlWithMcid => <ShareViaClipboard url={urlWithMcid} size="small" />}
    </ShareTracker>
    {repostProps && <ShareViaRepostAction itemType={itemType} repostProps={repostProps} />}
  </div>
);

/** Connects to redux before calling ShareDropdown. */
const ConnectedDropdownMenu = connect<DropdownProps, DropdownExternalProps, _, _, _, _>(
  (redux, externalProps: DropdownExternalProps) => ({
    ...externalProps,
    url: externalProps.url.startsWith('/')
      ? redux.meta.baseUrl + externalProps.url
      : externalProps.url,
    viewportCategory: redux.meta.device.viewportCategory,
  }),
)(ShareDropdownMenu);

/** Creates a popover which houses the share dropdown menu. */
const SharePopover = ({
  fixed,
  url,
  itemType,
  itemId,
  shiftX,
  shiftY,
  children,
  headerComponent,
  alignEdge,
  repostProps,
  above,
}: Props) => (
  <Popover
    local
    closeButton={false}
    fixed={fixed}
    above={above}
    below
    alignEdge={alignEdge}
    shiftX={shiftX}
    shiftY={shiftY}
    shiftArrow={shiftX}
    arrowType="small"
    contents={() => (
      <ConnectedDropdownMenu
        url={url}
        itemType={itemType}
        itemId={itemId}
        headerComponent={headerComponent}
        repostProps={repostProps}
      />
    )}
  >
    {children}
  </Popover>
);

SharePopover.defaultProps = {
  fixed: false,
  shiftX: 0,
  shiftY: 0,
  headerComponent: null,
  alignEdge: 'right',
};

export { SharePopover, ConnectedDropdownMenu as ShareDropdownMenu };
